import classNames from "classnames";

import { FC, PropsWithChildren, ReactElement, ReactNode, useEffect, useState } from "react";
import { TabsController, TabsProvider, useTabsContext } from "../../context/tabsContext";
import { Glyph, XOR } from "../../types";
import Icon from "./Icon";
import styles from "./Tabs.module.css";

export type Tab = {
  title: string;
  onClick?: () => void;
  disabled?: boolean;
  hasError?: boolean;
  hasSubTabs?: boolean;
  glyph?: Glyph;
};

type TabsProps = TabsInnerProps &
  XOR<
    {
      controller: TabsController;
    },
    {
      urlParameter: string;
    }
  >;

const Tabs = ({ controller, urlParameter, ...rest }: TabsProps) => (
  <TabsProvider urlParameterName={controller?.urlParameter ?? urlParameter}>
    <TabsInner {...rest}></TabsInner>
  </TabsProvider>
);

type TabsInnerProps = {
  tabs: Tab[];
  contents: ReactNode[];
  hideBorderBottom?: boolean;
  // Default render mode for tabs that don't set their own Tab.renderMode. "eager" (default) mounts
  // every tab's content upfront, kept in the DOM and only hidden via CSS. "lazy" defers mounting a
  // tab's content until it's selected at least once (then keeps it mounted), so tabs never visited
  // don't fire their data fetches.
  renderMode?: "eager" | "lazy";
};

const TabsInner: FC<TabsInnerProps> = ({ tabs, contents, hideBorderBottom, renderMode = "lazy" }: TabsInnerProps) => {
  const controller = useTabsContext();
  const selectedIndex = Math.min(controller.selectedIndex, tabs.length - 1);
  const selectedTab = tabs[selectedIndex];

  const [visitedIndexes, setVisitedIndexes] = useState<Set<number>>(() => new Set([selectedIndex]));

  useEffect(() => {
    if (!visitedIndexes.has(selectedIndex)) {
      setVisitedIndexes((prev) => new Set(prev).add(selectedIndex));
    }
  }, [selectedIndex]);

  return (
    <>
      <ul className={classNames(styles.ul, { [styles.hideBorderBottom]: hideBorderBottom })} role="tablist">
        {tabs.map((tab, tabIndex) => (
          <Tab
            key={`tab_${tabIndex}`}
            {...controller}
            selected={selectedIndex == tabIndex}
            error={tab.hasError}
            onClick={() => (tab.onClick ? tab.onClick() : controller.goToTab(tabIndex))}
            disabled={tab.disabled}
          >
            <span className={styles.tabInner}>
              {!!tab.glyph && <Icon glyph={tab.glyph} className={styles.tabIcon} />}
              <span className={styles.tabCopy}>
                <span className={styles.tabTitleRow}>
                  <span>{tab.title}</span>
                </span>
              </span>
            </span>
          </Tab>
        ))}
      </ul>
      {contents.map((tc, i) => (
        <div
          key={`content_${i}`}
          className={classNames(styles["tab-content"], {
            [styles.visible]: i === controller.selectedIndex || contents.length === 1,
            [styles.scroll]: !selectedTab?.hasSubTabs,
          })}
        >
          {renderMode === "eager" || visitedIndexes.has(i) || contents.length === 1 ? tc : null}
        </div>
      ))}
    </>
  );
};

type TabProps = PropsWithChildren<{
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
  error?: boolean;
}>;

const Tab: FC<TabProps> = (props: TabProps) => {
  const { children, disabled, selected, onClick, error } = {
    ...props,
  };

  return (
    <li
      role="presentation"
      className={classNames(styles.li, {
        [styles.active]: selected,
        [styles.disabled]: disabled,
        [styles.error]: error,
      })}
    >
      <button
        type="button"
        role="tab"
        aria-selected={selected}
        aria-disabled={disabled}
        className={styles.button}
        onClick={() => !disabled && onClick()}
      >
        {children}
      </button>
    </li>
  );
};

type SubTabProps = {
  children: ReactElement<TabsProps, typeof Tabs>;
};

const SubTab: FC<SubTabProps> = ({ children }) => <div className={styles["sub-tab"]}>{children}</div>;

Tabs.SubTab = SubTab;

export default Tabs;
